import React, { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Skeleton } from "antd";
import { Provider } from "react-redux";

import { RequestStatus, traineeActions } from "@fitness-app/app-store";

import { store, useAppDispatch, useAppSelector } from "~/store/initializeStore";
import { useTraineeScope } from "./TraineeScope";
import { traineeKeys } from "./traineeKeys";
import { TRAINEE_STORE_ABORT_REASON } from "./traineeStoreAbortReason";

// Jedna kolejka dla zmian klientki w Reduxie i operacji builderów. Część thunków builderów czyta
// klientkę albo program z Reduxa dopiero po `await` (np. przeciąganie treningu w kalendarzu
// zapisuje harmonogram do `traineeProgram.selectedProgram` z chwili zapisu). Gdyby w tym czasie
// karta B przejęła Reduxa, zapis z karty A trafiłby do klientki B. Dlatego zwolnienie i załadowanie
// klientki czekają w kolejce, aż operacje z poprzedniej karty się skończą.
let traineeStoreQueue: Promise<unknown> = Promise.resolve();

const enqueue = <T,>(operation: () => T | Promise<T>): Promise<T> => {
  const run = traineeStoreQueue.then(operation, operation);
  traineeStoreQueue = run.catch(() => undefined);
  return run;
};

/**
 * Uruchamia operację buildera, która czyta stan Reduxa po `await` (albo robi kilka kroków, np.
 * sprawdzenie i zapis), z zajętą kolejką: od startu do końca Redux nie przełączy się na klientkę
 * innej karty, a karta B pokaże w tym czasie szkielet. Nie zagnieżdżaj - wywołanie wewnątrz
 * innej takiej operacji czekałoby w kolejce na samo siebie.
 */
export const withTraineeStore = <T,>(traineeId: string, operation: () => Promise<T>): Promise<T> =>
  enqueue(() => {
    if (store.getState().trainee.profile?.id !== traineeId) {
      throw new Error("Ta klientka nie jest już załadowana w tej karcie. Otwórz kartę i spróbuj ponownie.");
    }
    return operation();
  });

// Odczyty builderów też: reducery `fetchMealsPlanWithDetails`, `fetchScheduledPrograms`, zapytań
// kalendarza itd. przyjmują spóźnione odpowiedzi bez sprawdzania klientki. Odpowiedź dla karty A,
// która przyszłaby po przejęciu Reduxa przez kartę B, pokazałaby w B dane A. Dlatego karta, która
// przejmuje Reduxa, najpierw czeka, aż skończą się thunki puszczone z builderów (tylko z nich -
// bramka podaje im własny `dispatch`, więc notatki czy ankiety z nagłówka nie są tu liczone). Te,
// które trwają dłużej niż limit, są przerywane (`abort()` z RTK): przerwany thunk nie wyśle już
// `fulfilled`, więc jego odpowiedź nie trafi do slice'ów, a wiszące zapytanie nie zablokuje kart.
// Samo wyjście z profilu niczego nie przerywa - nikt nie przejmuje Reduxa, więc nie ma po co
// (ADMIN-WEB-APP-8D: przerwany odczyt kalendarza po wyjściu na listę trafiał do Sentry).
const PENDING_THUNKS_TIMEOUT_MS = 5_000;
const pendingBuilderThunks = new Map<Promise<unknown>, { abort?: (reason?: string) => void }>();

const trackBuilderThunk = (result: unknown) => {
  if (result && typeof result === "object" && "requestId" in result) {
    const settled = Promise.resolve(result).then(
      () => undefined,
      () => undefined,
    );
    pendingBuilderThunks.set(settled, result as { abort?: (reason?: string) => void });
    void settled.then(() => pendingBuilderThunks.delete(settled));
  }
  return result;
};

// Store dla poddrzewa builderów: ten sam stan i subskrypcje, ale `dispatch` liczy puszczone thunki.
const builderStore: typeof store = {
  ...store,
  dispatch: ((action: Parameters<typeof store.dispatch>[0]) =>
    trackBuilderThunk(store.dispatch(action))) as typeof store.dispatch,
};

const waitForPendingBuilderThunks = async () => {
  const pending = [...pendingBuilderThunks.entries()];
  let timedOut = false;
  await Promise.race([
    Promise.all(pending.map(([settled]) => settled)),
    new Promise((resolve) =>
      setTimeout(() => {
        timedOut = true;
        resolve(undefined);
      }, PENDING_THUNKS_TIMEOUT_MS),
    ),
  ]);

  if (timedOut) {
    for (const [settled, thunk] of pending) {
      if (pendingBuilderThunks.has(settled)) {
        thunk.abort?.(TRAINEE_STORE_ABORT_REASON);
      }
    }
  }
};

/**
 * Buildery (program, kalendarz, dieta) zostają na Reduxie: ich thunki w app-store biorą
 * klientkę z `state.trainee.profile`, a slice'y trzymają jedną klientkę. Ten hook ładuje do
 * Reduxa klientkę karty, dopóki `enabled` - wołany raz na kartę (TraineeFeatures), żeby
 * przejście między zakładkami z builderami nie pobierało klientki od nowa. Włączaj go tylko
 * w widocznej karcie, wtedy w Reduxie siedzi najwyżej jedna klientka.
 */
export const useTraineeStoreOwnership = (enabled: boolean) => {
  const { traineeId } = useTraineeScope();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    enqueue(async () => {
      // Odczyty poprzedniej karty mogą być jeszcze w locie - kończymy je (albo przerywamy) i czyścimy
      // to, co zdążyły wpisać, zanim ta karta załaduje swoją klientkę.
      if (pendingBuilderThunks.size) {
        await waitForPendingBuilderThunks();
        dispatch(traineeActions.unsubscribeFromTrainee());
      }
      return dispatch(traineeActions.subscribeToTrainee(traineeId));
    });

    return () => {
      enqueue(() => {
        dispatch(traineeActions.unsubscribeFromTrainee());
        // Builder pisał przez Redux - reszta karty czyta przez react-query, więc ją odświeżamy,
        // także po zapisach, które jeszcze trwają.
        const invalidate = () => queryClient.invalidateQueries({ queryKey: traineeKeys.all(traineeId) });
        void invalidate();
        void Promise.all([...pendingBuilderThunks.keys()]).then(invalidate);
      });
    };
  }, [enabled, dispatch, queryClient, traineeId]);
};

/**
 * Renderuje builder dopiero, gdy Redux ma klientkę tej karty (patrz `useTraineeStoreOwnership`).
 * Wewnątrz bramki `store.trainee.profile` i slice'y `trainee*` należą do tej karty.
 * Poza nią profil czyta dane wyłącznie przez react-query.
 */
export const TraineeStoreGate = ({ children }: { children: React.ReactNode }) => {
  const { traineeId, isActive } = useTraineeScope();
  const profileId = useAppSelector((store) => store.trainee.profile?.id);
  const profileStatus = useAppSelector((store) => store.trainee.profileStatus);

  if (!isActive) {
    return null;
  }

  if (profileId !== traineeId) {
    if (profileStatus === RequestStatus.FAILED) {
      return <Alert type="error" message="Nie udało się pobrać profilu" />;
    }

    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  return <Provider store={builderStore}>{children}</Provider>;
};
